import React from 'react';
import styled from 'styled-components';
import SEO from '../../components/common/SEO';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import theme from '../../theme';

const IntegrationSection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto;
`;

const ContentSection = styled.div`
  margin-bottom: 5rem;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  &:nth-child(even) {
    direction: rtl;
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      direction: ltr;
    }
  }
  
  &:nth-child(even) > div {
    direction: ltr;
  }
`;

const ContentText = styled.div`
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
  }
  
  ul {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.75rem;
      line-height: 1.7;
    }
  }
`;

const ContentImage = styled.div`
  img {
    width: 100%;
    height: auto;
    border-radius: ${theme.borderRadius};
    box-shadow: ${theme.boxShadow};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const IntegrationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  margin-top: 3rem;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const IntegrationCard = styled.div`
  background-color: ${theme.colors.lightGray};
  padding: 2rem;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  text-align: center;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.boxShadow};
  }
`;

const IntegrationLogo = styled.div`
  width: 120px;
  height: 120px;
  background-color: ${theme.colors.white};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  
  img {
    max-width: 70%;
    max-height: 70%;
  }
`;

const IntegrationTitle = styled.h3`
  font-size: ${theme.fontSizes.medium};
  margin-bottom: 1rem;
`;

const IntegrationDescription = styled.p`
  color: ${theme.colors.darkGray};
  line-height: 1.6;
  text-align: left;
`;

const StepsContainer = styled.div`
  margin-top: 3rem;
`;

const Step = styled.div`
  display: flex;
  margin-bottom: 2rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin-right: 1.5rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: 1rem;
  }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  margin-bottom: 0.5rem;
  font-size: ${theme.fontSizes.medium};
`;

const StepDescription = styled.p`
  line-height: 1.7;
`;

const CTASection = styled.section`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  max-width: 700px;
  margin: 0 auto 2rem;
  font-size: ${theme.fontSizes.medium};
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  padding: 1rem 2rem;
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  
  &:hover {
    background-color: ${theme.colors.accent};
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const IntegrationPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();

  const erpSystems = [
    { key: 'sap', logo: `${process.env.PUBLIC_URL}/sap_logo.webp` },
    { key: 'logo', logo: `${process.env.PUBLIC_URL}/logo_yazilim_logo.jpeg` },
    { key: 'mikro', logo: `${process.env.PUBLIC_URL}/mikro_logo.png` },
    { key: 'dynamics', logo: `${process.env.PUBLIC_URL}/ms_dynamics_logo.jpeg` },
    { key: 'oracle', logo: `${process.env.PUBLIC_URL}/Oracle_logo.svg` },
  ];

  const stepKeys = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6'];

  return (
    <>
      <SEO titleKey="integrationSolutionsPage.seo.title" descriptionKey="integrationSolutionsPage.seo.description" />
      
      <IntegrationSection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t('integrationPage.pageTitle')}</PageTitle>
            <PageSubtitle>{t('integrationPage.pageSubtitle')}</PageSubtitle>
          </PageHeader>
          
          <ContentSection>
            <TwoColumnLayout>
              <ContentText>
                <SectionTitle>{t('integrationPage.erp.title')}</SectionTitle>
                <p>{t('integrationPage.erp.paragraph1')}</p>
                <p>{t('integrationPage.erp.paragraph2')}</p>
                <ul>
                  <li>{t('integrationPage.erp.bullet1')}</li>
                  <li>{t('integrationPage.erp.bullet2')}</li>
                  <li>{t('integrationPage.erp.bullet3')}</li>
                  <li>{t('integrationPage.erp.bullet4')}</li>
                  <li>{t('integrationPage.erp.bullet5')}</li>
                </ul>
              </ContentText>
              <ContentImage>
                <img src={`${process.env.PUBLIC_URL}/integrations-1.jpeg`} alt={t('integrationPage.erp.imageAlt')} loading="lazy" />
              </ContentImage>
            </TwoColumnLayout>
            
            <IntegrationGrid>
              {erpSystems.map((system) => (
                <IntegrationCard key={system.key}>
                  <IntegrationLogo>
                    <img src={system.logo} alt={t(`integrationPage.erpSystems.${system.key}.title`)} loading="lazy" />
                  </IntegrationLogo>
                  <IntegrationTitle>{t(`integrationPage.erpSystems.${system.key}.title`)}</IntegrationTitle>
                  <IntegrationDescription>{t(`integrationPage.erpSystems.${system.key}.description`)}</IntegrationDescription>
                </IntegrationCard>
              ))}
            </IntegrationGrid>
            
            <TwoColumnLayout>
              <ContentText>
                <SectionTitle>{t('integrationPage.accounting.title')}</SectionTitle>
                <p>{t('integrationPage.accounting.paragraph1')}</p>
                <p>{t('integrationPage.accounting.paragraph2')}</p>
                <ul>
                  <li>{t('integrationPage.accounting.bullet1')}</li>
                  <li>{t('integrationPage.accounting.bullet2')}</li>
                  <li>{t('integrationPage.accounting.bullet3')}</li>
                  <li>{t('integrationPage.accounting.bullet4')}</li>
                  <li>{t('integrationPage.accounting.bullet5')}</li>
                </ul>
              </ContentText>
              <ContentImage>
                <img src={`${process.env.PUBLIC_URL}/integrations-2.jpeg`} alt={t('integrationPage.accounting.imageAlt')} loading="lazy" />
              </ContentImage>
            </TwoColumnLayout>
          </ContentSection>
          
          <StepsContainer>
            <SectionTitle style={{ textAlign: 'center', marginBottom: '3rem' }}>
              {t('integrationPage.steps.title')}
            </SectionTitle>
            
            {stepKeys.map((key, index) => (
              <Step key={key}>
                <StepNumber>{index + 1}</StepNumber>
                <StepContent>
                  <StepTitle>{t(`integrationPage.steps.${key}.title`)}</StepTitle>
                  <StepDescription>{t(`integrationPage.steps.${key}.description`)}</StepDescription>
                </StepContent>
              </Step>
            ))}
          </StepsContainer>
        </div>
      </IntegrationSection>
      
      <CTASection>
        <div className="container">
          <CTATitle>{t('integrationPage.cta.title')}</CTATitle>
          <CTAText>{t('integrationPage.cta.text')}</CTAText>
          <CTAButton to={`/${lang}/demo`}>{t('integrationPage.cta.button')}</CTAButton>
        </div>
      </CTASection>
    </>
  );
};

export default IntegrationPage;
